import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useSafeRouteParams } from '@sg/garnish';

import { Receipt, useReceiptMachine } from '@order/features/receipt';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTrackEffect } from '@order/Telemetry';

import { LoadingAnimation, SignedOutView, NotFoundView } from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import { useCustomerData } from '../../context/Customer/hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ReceiptClaimScreen = () => {
  const { receiptId } = useSafeRouteParams<{ receiptId: string }>();

  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  const isReceiptScreenEnabled = useFeatureFlag('cels-4664-enable-receipt-screen');

  useTrackEffect('receipt-claim.screen_view');

  // ─── Gates ─────────────────────────────────────────────────────────────────

  if (!isReceiptScreenEnabled) return <NotFoundView />;

  if (isLoggingIn) return <LoadingAnimation />;

  if (!isLoggedIn) {
    return (
      <SignedOutView
        redirectTo="receipt-claim"
        redirectToParams={{ redirectToReceiptId: receiptId }}
      />
    );
  }

  return <ReceiptClaimScreenContent receiptId={receiptId} />;
};

// ─── Content ─────────────────────────────────────────────────────────────────

const ReceiptClaimScreenContent = ({ receiptId }: ReceiptClaimScreenContentProps) => {
  const { formatMessage } = useIntl();
  const { customer } = useCustomerData();

  const {
    isFetchingReceipt,
    isClaiming,
    isClaimed,
    isExpiredError,
    isReceiptAlreadyClaimedError,
    isTimeRestrictionError,
    isTransactionNotFoundError,
    isUnknownError,
  } = useReceiptMachine(receiptId);

  // ─── Loading ───────────────────────────────────────────────────────────────────

  if (isFetchingReceipt || isClaiming) return <LoadingAnimation />;

  // ─── Success ───────────────────────────────────────────────────────────────────

  if (isClaimed) {
    return (
      <Receipt.Claim.Container>
        <Receipt.Claim.Success name={customer.firstName} />
      </Receipt.Claim.Container>
    );
  }

  // ─── Error States ──────────────────────────────────────────────────────────────

  if (isReceiptAlreadyClaimedError) {
    return <CantClaimView description={formatMessage(messages.claimedDescription)} />;
  }

  if (isExpiredError) {
    return <CantClaimView description={formatMessage(messages.expiredDescription)} />;
  }

  if (isTimeRestrictionError) {
    return <CantClaimView description={formatMessage(messages.timeRestrictionDescription)} />;
  }

  if (isTransactionNotFoundError) {
    return <NotFoundView />;
  }

  if (isUnknownError) {
    return <UndefinedErrorView />;
  }

  return null;
};

// ─── Unavailable Claim Views ─────────────────────────────────────────────────

const CantClaimView = ({ description }: CantClaimViewProps) => {
  const { formatMessage } = useIntl();

  return (
    <Receipt.UnavailableView
      title={formatMessage(messages.cantClaimTitle)}
      description={description}
      shouldDisplayContactUs
    />
  );
};

const UndefinedErrorView = () => {
  const { formatMessage } = useIntl();

  return <Receipt.UnavailableView title={formatMessage(messages.errorTitle)} />;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  cantClaimTitle: {
    defaultMessage: 'This receipt is unavailable to claim',
    description: "Receipt | Can't Claim View | Title",
  },
  expiredDescription: {
    defaultMessage: 'This receipt has expired.',
    description: 'Receipt | Expired View | Description',
  },
  timeRestrictionDescription: {
    defaultMessage: 'You have claimed too many orders today. Please try again later.',
    description: 'Receipt | Time Restriction View | Description',
  },
  claimedDescription: {
    defaultMessage: 'This receipt has already been claimed.',
    description: 'Receipt | Already Claimed View | Description',
  },
  errorTitle: {
    defaultMessage: 'We encountered an error. Please try again later.',
    description: 'Receipt Claim | Unknown Error View | Title',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ReceiptClaimScreenContentProps = {
  receiptId: string;
};

type CantClaimViewProps = {
  description: string;
};
